import { normalizeCharacterIP, type CharacterIPMedia, type CharacterIPValue } from '../../components/CharacterIPPanel/types';
import { MyIpRequestError } from './myIpService';
import { getMyIpProfileDisplay } from './profileDisplay';
import { getMyIpMediaUrl, getMyIpMediaResourceKey } from './mediaUrl';
import { isMyIpGeneratedImage } from './imageSource';
import { getMyIpMediaReferenceUrl, getMyIpMediaReferences } from './mediaReferences';
import { getMyIpDetailMedia } from './detailMedia';
import {
  getMyIpMaterialObject, mergeMyIpAgentMaterials,
} from './materialCollection';
import { getExtensionFromAssetUrl } from '../../utils/assetLibraryCardLayout';
import { getMyIpSelectedVoice, preserveMyIpVoiceSelection } from './voiceSelection';
import {
  getMyIpResourceSnapshot, omitMyIpProfileMaterials, upsertMyIpResource,
  materialToMyIpResource, getMyIpResourceKey,
} from './resourceSlots';
import type { MyIpDraftSnapshot, MyIpItem, MyIpProfile } from './types';

// 创建页与通用面板共用产品字段表，避免叠加旧主档字段的必填要求。
export { CHARACTER_IP_REQUIRED_FIELDS as MY_IP_COMPLETE_FIELDS } from '../../components/CharacterIPPanel/types';

/** 摘要和 profile 都是 JSON 对象；拒绝文本、数组，不能解析聊天 Markdown 充当业务数据。 */
function object(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new MyIpRequestError('Invalid IP character snapshot');
  return value as Record<string, unknown>;
}

/** 保留签名参数原文；只接受明确的 HTTP(S) 媒体地址，不把 URL 当成资源关系 ID。 */
function mediaUrl(value: unknown): string {
  if (value === '') return '';
  if (typeof value !== 'string') throw new MyIpRequestError('Invalid IP character media');
  const parsed = new URL(value);
  if (!['http:', 'https:'].includes(parsed.protocol) || parsed.username || parsed.password) {
    throw new MyIpRequestError('Invalid IP character media URL');
  }
  return value;
}

/** 新建草稿补齐完整结构，编辑已有 IP 时以 detail 数据为底稿；初始化本身不发起保存。 */
export function initializeMyIpCreationItem(item: MyIpItem): MyIpItem {
  return {
    ...item,
    profile: {
      ...item.profile,
      character_setting: {
        identity: '',
        personality: [],
        appearance: '',
        speech_style: '',
        backstory: '',
        ...item.profile.character_setting,
      },
    },
  };
}

/** 只有显式下发 resources 的摘要才接管内存资源集合，文案片段不改变集合来源。 */
export function hasMyIpAgentResources(raw: unknown): boolean {
  const envelope = object(raw);
  return Object.hasOwn(envelope, 'resources')
    || (Object.hasOwn(envelope, 'ip_character') && Object.hasOwn(object(envelope.ip_character), 'resources'));
}

/** 保存/刷新仅补回现有内存资源的服务端身份和签名，不把未删除的旧关系重新追加到面板。 */
export function refreshMyIpMemoryResources(local: MyIpItem['resources'], latest: MyIpItem['resources']): MyIpItem['resources'] {
  return local.map((resource) => {
    const persisted = latest.find((candidate) => candidate.role === resource.role
      && getMyIpResourceKey(candidate) === getMyIpResourceKey(resource));
    if (!persisted) return resource;
    // 手工换图已明确清空旧水印；服务端回包若仍带旧水印，不能覆盖这次编辑意图。
    const watermarkUrl = resource.watermarkUrl === '' ? '' : persisted.watermarkUrl || resource.watermarkUrl;
    return {
      ...persisted,
      ...resource,
      id: persisted.id || resource.id,
      assetId: persisted.assetId || resource.assetId,
      url: persisted.url || resource.url,
      ...(watermarkUrl === undefined ? {} : { watermarkUrl }),
      available: persisted.available,
    };
  });
}

/** 内存角色是展示与保存的同一底稿；响应只补版本、扩展元数据和同一媒体的授权地址。 */
export function refreshMyIpCreationMemory(local: MyIpItem, latest: MyIpItem): MyIpItem {
  const sameCover = getMyIpMediaResourceKey(local.coverUrl || local.coverAssetId)
    === getMyIpMediaResourceKey(latest.coverUrl || latest.coverAssetId);
  const sameAvatar = getMyIpMediaResourceKey(local.avatarUrl) === getMyIpMediaResourceKey(latest.avatarUrl);
  return {
    ...latest,
    name: local.name,
    summary: local.summary,
    coverAssetId: sameCover ? latest.coverAssetId : local.coverAssetId,
    coverUrl: sameCover ? latest.coverUrl : local.coverUrl,
    coverWatermarkUrl: sameCover ? latest.coverWatermarkUrl : local.coverWatermarkUrl,
    avatarUrl: sameAvatar ? latest.avatarUrl : local.avatarUrl,
    avatarWatermarkUrl: sameAvatar ? latest.avatarWatermarkUrl : local.avatarWatermarkUrl,
    profile: mergeMyIpCreationProfile(latest.profile, local.profile),
    resources: refreshMyIpMemoryResources(local.resources, latest.resources),
  };
}

/** 摘要只携带本次生成的片段；字段缺省不补空，避免覆盖完整底稿。 */
function normalizeAgentCharacter(item: MyIpItem, raw: unknown): Record<string, unknown> {
  const envelope = object(raw);
  const updateShaped = Object.hasOwn(envelope, 'profile') || Object.hasOwn(envelope, 'cover_url') || Object.hasOwn(envelope, 'avatar_url')
    || Object.hasOwn(envelope, 'cover_watermark_url') || Object.hasOwn(envelope, 'avatar_watermark_url')
    || Object.hasOwn(envelope, 'resources');
  if (!Object.hasOwn(envelope, 'ip_character') && !updateShaped) return envelope;

  const character = Object.hasOwn(envelope, 'ip_character') ? object(envelope.ip_character) : envelope;
  const profile = object(character.profile ?? {});
  /** 旧摘要可能携带内部路径，这类封面继续使用 detail 已授权的展示地址。 */
  const validUrl = (value: unknown): string => {
    try { return mediaUrl(value); } catch { return ''; }
  };
  const currentMainVisual = validUrl(item.coverUrl || getMyIpMediaUrl(item.coverAssetId));

  const {
    profile: ignoredProfile,
    cover_url: ignoredCoverUrl,
    avatar_url: ignoredAvatarUrl,
    cover_watermark_url: ignoredCoverWatermarkUrl,
    avatar_watermark_url: ignoredAvatarWatermarkUrl,
    role_card_watermark_url: ignoredRoleCardWatermarkUrl,
    resources: ignoredResources,
    ...fields
  } = character;
  const coverValue = Object.hasOwn(envelope, 'cover_url') ? envelope.cover_url : character.cover_url;
  const avatarValue = Object.hasOwn(envelope, 'avatar_url') ? envelope.avatar_url : character.avatar_url;
  const coverWatermarkValue = Object.hasOwn(envelope, 'cover_watermark_url')
    ? envelope.cover_watermark_url : character.cover_watermark_url;
  const avatarWatermarkValue = Object.hasOwn(envelope, 'avatar_watermark_url')
    ? envelope.avatar_watermark_url : character.avatar_watermark_url;
  return {
    ...profile,
    ...fields,
    // update 的 cover_url 空串表示显式清空；字段缺省或内部相对路径才沿用 detail 的授权地址。
    ...(coverValue === undefined ? {} : { main_visual: coverValue === '' ? '' : validUrl(coverValue) || currentMainVisual }),
    ...(avatarValue === undefined ? {} : { avatar_url: avatarValue === '' ? '' : validUrl(avatarValue) || item.avatarUrl || '' }),
    ...(coverWatermarkValue === undefined ? {} : {
      cover_watermark_url: coverWatermarkValue === '' ? '' : validUrl(coverWatermarkValue),
    }),
    ...(avatarWatermarkValue === undefined ? {} : {
      avatar_watermark_url: avatarWatermarkValue === '' ? '' : validUrl(avatarWatermarkValue) || item.avatarWatermarkUrl || '',
    }),
    ...(Object.hasOwn(envelope, 'resources') || Object.hasOwn(character, 'resources')
      ? { resources: Object.hasOwn(envelope, 'resources') ? envelope.resources : character.resources } : {}),
  };
}

/** 合并主档未知扩展键及设定未知键；已提供的空字符串/空数组按完整快照明确替换。 */
export function mergeMyIpCreationProfile(base: MyIpProfile, next: MyIpProfile): MyIpProfile {
  return {
    ...base,
    ...next,
    ...(next.character_setting ? {
      character_setting: { ...base.character_setting, ...next.character_setting },
    } : {}),
  };
}

/** resources 是完整资源片段，整段替换；仅复用 detail 的关系身份，不追加旧资源。 */
function replaceAgentResources(item: MyIpItem, raw: unknown): MyIpItem['resources'] {
  if (!Array.isArray(raw)) throw new MyIpRequestError('Invalid IP character resources');
  return raw.map((entry, index) => {
    const candidate = materialToMyIpResource(entry);
    const record = object(entry);
    // 历史摘要的内部媒体路径不能直接播放，沿用同关系或单槽位的授权详情。
    const previous = item.resources.find((resource) => (
      (record.id !== undefined && resource.id === String(record.id))
      || (candidate && resource.role === candidate.role
        && (getMyIpResourceKey(resource) === getMyIpResourceKey({ ...resource, ...candidate })
          || candidate.role !== 'additional_image'))
      || (!candidate && resource.role === record.resource_role && resource.role !== 'additional_image')
    ));
    if (!candidate) {
      if (previous) return { ...previous };
      throw new MyIpRequestError('Invalid IP character resource');
    }
    return {
      assetId: '',
      status: 'active',
      ...previous,
      ...candidate,
      id: previous?.id || '',
      sortOrder: candidate.sortOrder ?? index,
      // 原图换了但摘要未带新水印时清空旧水印，避免错配；有 watermark_url 时 candidate 已覆盖。
      ...(previous && candidate.url
        && getMyIpMediaResourceKey(previous.url) !== getMyIpMediaResourceKey(candidate.url)
        && candidate.watermarkUrl === undefined ? { watermarkUrl: '' } : {}),
    };
  });
}

/** 将摘要片段覆盖到完整底稿，缺省字段保留；版本和 IP 身份始终由已授权详情提供。 */
export function getMyIpAgentSnapshot(item: MyIpItem, raw: unknown): MyIpDraftSnapshot {
  const patch = normalizeAgentCharacter(item, raw);
  const initial = initializeMyIpCreationItem(item);
  const character = {
    name: item.name,
    main_visual: item.coverUrl || getMyIpMediaUrl(item.coverAssetId) || '',
    turnaround_visual: '',
    materials: [],
    ...patch,
    character_setting: {
      ...initial.profile.character_setting,
      ...(patch.character_setting === undefined ? {} : object(patch.character_setting)),
    },
  } as Record<string, unknown>;
  const setting = object(character.character_setting);
  if (typeof character.name !== 'string' || !Array.isArray(character.materials)
    || !Array.isArray(setting.personality) || setting.personality.some((value) => typeof value !== 'string')
    || ['identity', 'appearance'].some((key) => typeof setting[key] !== 'string')
    || ['speech_style', 'backstory'].some((key) => setting[key] !== undefined && setting[key] !== null
      && typeof setting[key] !== 'string')
    || (character.summary !== undefined && typeof character.summary !== 'string')) {
    throw new MyIpRequestError('Invalid IP character fields');
  }
  const sync = getMyIpMaterialObject(item.profile.material_sync);
  const baseProfile = { ...item.profile, ...(sync?.schema_version === 1 ? { material_sync: { ...sync, additions: [] } } : {}) };
  const mainVisual = mediaUrl(character.main_visual);
  const turnaroundVisual = mediaUrl(character.turnaround_visual);
  const {
    name, summary, resources: ignoredResources, avatar_url: avatarUrl,
    cover_watermark_url: coverWatermarkUrl, avatar_watermark_url: avatarWatermarkUrl,
    ip_id: ignoredId, version: ignoredVersion, ip_version: ignoredIpVersion,
    ...profile
  } = character;
  let coverWatermarkPatch: { coverWatermarkUrl?: string; } = {};
  if (Object.hasOwn(patch, 'main_visual')) {
    coverWatermarkPatch = { coverWatermarkUrl: Object.hasOwn(patch, 'cover_watermark_url') ? String(coverWatermarkUrl ?? '') : '' };
  } else if (Object.hasOwn(patch, 'cover_watermark_url')) {
    coverWatermarkPatch = { coverWatermarkUrl: String(coverWatermarkUrl ?? '') };
  }
  let avatarWatermarkPatch: { avatarWatermarkUrl?: string; } = {};
  if (Object.hasOwn(patch, 'avatar_url')) {
    avatarWatermarkPatch = { avatarWatermarkUrl: Object.hasOwn(patch, 'avatar_watermark_url') ? String(avatarWatermarkUrl ?? '') : '' };
  } else if (Object.hasOwn(patch, 'avatar_watermark_url')) {
    avatarWatermarkPatch = { avatarWatermarkUrl: String(avatarWatermarkUrl ?? '') };
  }
  const snapshot = getMyIpResourceSnapshot(item, {
    name,
    summary: typeof summary === 'string' ? summary : item.summary,
    coverAssetId: Object.hasOwn(patch, 'main_visual') ? mainVisual : item.coverAssetId,
    ...(Object.hasOwn(patch, 'avatar_url') ? { avatarUrl: avatarUrl as string } : {}),
    // 封面换了但摘要未带新水印时清空旧水印，避免原图与水印地址错配；水印单独下发时只更新水印。
    ...coverWatermarkPatch,
    ...avatarWatermarkPatch,
    profile: JSON.parse(JSON.stringify(preserveMyIpVoiceSelection(item.profile, mergeMyIpAgentMaterials(baseProfile, mergeMyIpCreationProfile(baseProfile, {
      ...profile,
      character_setting: setting,
      ...(patch.character_setting !== undefined && typeof character.persona !== 'string' && typeof item.profile.persona !== 'string'
        ? { persona: setting.personality.join('\n') } : {}),
      ...(patch.character_setting !== undefined && Object.hasOwn(object(patch.character_setting), 'speech_style')
        && typeof character.language_style !== 'string' && typeof setting.speech_style === 'string'
        ? { language_style: setting.speech_style } : {}),
      main_visual: mainVisual,
      turnaround_visual: turnaroundVisual,
    }))))),
  });
  if (Object.hasOwn(patch, 'resources')) return { ...snapshot, resources: replaceAgentResources(item, patch.resources) };
  // 只有旧媒体协议才需要槽位转换；普通文案摘要不能顺带重排或筛掉既有资源。
  return Object.hasOwn(patch, 'materials') || Object.hasOwn(patch, 'turnaround_visual')
    ? snapshot : { ...snapshot, resources: item.resources };
}

/** 失败队列里的生成结果重放到新版本，服务端新扩展键优先，已约定的生成字段保留最新产物。 */
export function getMyIpAgentRetrySnapshot(item: MyIpItem, raw: unknown): MyIpDraftSnapshot {
  const snapshot = getMyIpAgentSnapshot(item, raw);
  const setting = snapshot.profile.character_setting!;
  return {
    ...snapshot,
    profile: {
      ...snapshot.profile,
      ...omitMyIpProfileMaterials(item.profile),
      // 已约定字段使用本次产物，避免新映射被重读版本里的旧兼容字段覆盖。
      ...Object.fromEntries(['persona', 'language_style', 'visual_style', 'voice']
        .filter((key) => Object.hasOwn(snapshot.profile, key)).map((key) => [key, snapshot.profile[key]])),
      character_setting: {
        ...setting,
        ...item.profile.character_setting,
        identity: setting.identity,
        personality: setting.personality,
        appearance: setting.appearance,
        ...(setting.speech_style === undefined ? {} : { speech_style: setting.speech_style }),
        ...(setting.backstory === undefined ? {} : { backstory: setting.backstory }),
      },
    },
  };
}

/** 只合并用户相对编辑起点改动的文本；摘要中尚未生成的必填字段仍可保持为空。 */
export function mergeMyIpTextEditSnapshot(item: MyIpItem, value: CharacterIPValue, initialValue: CharacterIPValue): MyIpDraftSnapshot {
  if ((value.id && value.id !== item.ipId) || (initialValue.id && initialValue.id !== item.ipId)) {
    throw new MyIpRequestError('IP text edit context changed');
  }
  const edited = normalizeCharacterIP(value);
  const initial = normalizeCharacterIP(initialValue);
  const changes: Record<string, unknown> = {};
  (['identity', 'personality', 'appearance', 'speech_style', 'backstory'] as const).forEach((key) => {
    const next = edited[key] ?? '';
    if (JSON.stringify(next) !== JSON.stringify(initial[key] ?? '')) changes[key] = next;
  });
  return {
    name: edited.name === initial.name ? item.name : edited.name,
    summary: edited.summary === initial.summary ? item.summary : edited.summary ?? '',
    coverAssetId: item.coverAssetId,
    profile: mergeMyIpCreationProfile(item.profile, {
      character_setting: { ...item.profile.character_setting, ...changes },
      ...(edited.persona !== initial.persona ? { persona: edited.persona ?? '' } : {}),
      ...(edited.visual_style !== initial.visual_style ? { visual_style: edited.visual_style ?? '' } : {}),
      ...(edited.speech_style !== initial.speech_style ? { language_style: edited.speech_style ?? '' } : {}),
    }),
  };
}

/** 用户显式保存校验最终合并后的文字，保留未编辑字段和媒体，但不能保存隐含的空必填值。 */
export function getMyIpTextEditSnapshot(item: MyIpItem, value: CharacterIPValue, initialValue: CharacterIPValue): MyIpDraftSnapshot {
  const snapshot = mergeMyIpTextEditSnapshot(item, value, initialValue);
  const merged = getMyIpCreationValue({ ...item, ...snapshot });
  if (!merged.name.trim() || !merged.identity.trim() || !merged.appearance.trim()) {
    throw new MyIpRequestError('Missing IP character text');
  }
  return snapshot;
}

/** 文字编辑携带待保存资源，图片和音色仅覆盖用户实际改动的槽位。 */
export function mergeMyIpCreationEditSnapshot(item: MyIpItem, value: CharacterIPValue, initialValue: CharacterIPValue): MyIpDraftSnapshot {
  const snapshot = { ...mergeMyIpTextEditSnapshot(item, value, initialValue), resources: item.resources };
  if (JSON.stringify(value.voice ?? null) !== JSON.stringify(initialValue.voice ?? null)) {
    if (value.voice && (value.voice.kind !== 'audio' || !getMyIpMediaUrl(value.voice.url))) throw new MyIpRequestError('Invalid IP voice URL');
    snapshot.resources = value.voice
      ? upsertMyIpResource(item.resources, {
        role: 'voice',
        type: 'audio',
        url: value.voice.url,
        name: value.voice.name,
        voiceId: String(value.voice.voiceId ?? value.voice.voicePreset?.voice_id ?? value.voice.timbre?.voice_id ?? ''),
      })
      : item.resources.filter((resource) => resource.role !== 'voice');
  }
  (['main_visual', 'turnaround'] as const).forEach((field) => {
    if (value[field]?.url === initialValue[field]?.url) return;
    const url = getMyIpMediaUrl(value[field]?.url);
    if (value[field]?.kind !== 'image' || !url) throw new MyIpRequestError('Invalid IP replacement image');
    // 手工替换不继承旧生成来源，避免上传图被误加水印。
    if (Array.isArray(snapshot.profile.generated_image_keys)) {
      snapshot.profile = { ...snapshot.profile, generated_image_keys: snapshot.profile.generated_image_keys.filter((key) => key !== getMyIpMediaResourceKey(url)) };
    }
    if (field === 'main_visual') {
      snapshot.coverAssetId = url;
      // 手工替换图不再沿用旧生成水印，避免原图与水印地址错配。
      snapshot.coverWatermarkUrl = '';
    } else {
      snapshot.resources = upsertMyIpResource(snapshot.resources || item.resources, {
        role: 'three_view', type: 'image', url, name: value[field]?.name, watermarkUrl: '',
      });
    }
  });
  return snapshot;
}

/** 创建页中间保存允许不完整草稿；最终完成由队列和共享必填字段表校验。 */
export function getMyIpCreationEditSnapshot(item: MyIpItem, value: CharacterIPValue, initialValue: CharacterIPValue): MyIpDraftSnapshot {
  return mergeMyIpCreationEditSnapshot(item, value, initialValue);
}

/** 画布和独立面板沿用同一增量合并，保留编辑期间到达的媒体及未知扩展。 */
export function getMyIpEditedSnapshot(item: MyIpItem, value: CharacterIPValue, previous: CharacterIPValue): MyIpDraftSnapshot {
  return mergeMyIpCreationEditSnapshot(item, value, previous);
}

/** URL 仅作为面板预览标识；不把它作为资源关系 ID。 */
function previewImage(
  value: unknown,
  name: string,
  profile: MyIpProfile,
  watermarkUrl?: string,
  size?: { width?: number; height?: number; },
  assetId?: string,
): CharacterIPMedia | null {
  if (!value) return null;
  try {
    const url = mediaUrl(value);
    return {
      id: url,
      url,
      name,
      kind: 'image',
      ...(isMyIpGeneratedImage(profile, url) ? { source: 'generated' as const } : {}),
      ...(getMyIpMediaUrl(watermarkUrl) ? { watermarkUrl: getMyIpMediaUrl(watermarkUrl) } : {}),
      ...(assetId && !getMyIpMediaUrl(assetId) ? { assetId } : {}),
      ...(typeof size?.width === 'number' && size.width > 0 ? { width: size.width } : {}),
      ...(typeof size?.height === 'number' && size.height > 0 ? { height: size.height } : {}),
    };
  } catch { return null; }
}

/** 创建页和刷新恢复使用同一授权媒体映射，不通过再次重放历史消息写入旧数据。 */
export function getMyIpCreationValue(item: MyIpItem): CharacterIPValue {
  const display = getMyIpProfileDisplay(item.profile);
  const personality = item.profile.character_setting?.personality;
  const media = getMyIpDetailMedia(item);
  return {
    id: item.ipId,
    name: item.name,
    summary: item.summary,
    persona: typeof item.profile.persona === 'string' ? item.profile.persona : display.personality.join('\n'),
    visual_style: display.visualStyle,
    identity: display.identity,
    personality: Array.isArray(personality)
      ? personality.filter((value) => typeof value === 'string') : display.personality,
    appearance: display.appearance,
    speech_style: display.speechStyle,
    backstory: display.backstory,
    main_visual: previewImage(item.coverUrl || getMyIpMediaUrl(item.coverAssetId), item.name, item.profile, item.coverWatermarkUrl, undefined, item.coverAssetId),
    // 复用详情媒体映射，保证创建/编辑表单与详情页采用相同的头像回退规则。
    avatar: previewImage(media.avatarUrl, item.name, item.profile, media.avatarWatermarkUrl),
    turnaround: previewImage(
      media.threeViews[0]?.url,
      item.name,
      item.profile,
      media.threeViews[0]?.watermarkUrl,
      media.threeViews[0],
      media.threeViews[0]?.assetId,
    ),
    voice: getMyIpSelectedVoice(item),
    materials: getMyIpMediaReferences(item).references.filter((reference) => ['additional_image', 'video'].includes(reference.role)).flatMap((reference) => {
      const url = getMyIpMediaReferenceUrl(reference);
      return url && reference.type !== 'unknown' ? [{
        id: reference.assetId || url,
        url,
        kind: reference.type,
        name: reference.name,
        thumbnailUrl: reference.thumbnailUrl,
        watermarkUrl: reference.watermarkUrl,
        watermarkCoverUrl: reference.watermarkCoverUrl,
        assetId: reference.assetId,
        duration: reference.duration,
        fileSize: reference.fileSize,
        format: reference.fileExtension || getExtensionFromAssetUrl(url) || reference.type,
        ...(reference.width === undefined ? {} : { width: reference.width }),
        ...(reference.height === undefined ? {} : { height: reference.height }),
      }] : [];
    }),
  };
}
